import { deleteCookie, getCookie, setCookie } from "@/utils/cookies";

/**
 * The visitor ids the backend reads from cookies (`tracking/middleware.py`).
 *
 * `_tid` is the visitor's tracking id: the account they create takes it, a
 * signed-in user's is their account's, and PostHog knows the person by it.
 * `_sid` and `_did`, the Google Analytics session and client id, go on their
 * purchases as `$session_id` and `$device_id`.
 */

const YEAR = 365 * 24 * 60 * 60;
const GA_SESSION = 30 * 60;
const GA4_STREAM = "Z1CFG0XGWL";

function cookieOptions(maxAge?: number) {
  return {
    domain: process.env.NEXT_PUBLIC_COOKIE_DOMAIN,
    secure: true,
    // The backend reads values undecoded, and GA's session holds `$`.
    encode: String,
    maxAge,
  };
}

/** A version 4 UUID, which is what the backend parses `_tid` as. */
function uuid(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) =>
    byte.toString(16).padStart(2, "0")
  ).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** The visitor's tracking id, made on their first visit. */
export function trackingId(): string {
  const existing = getCookie("_tid");
  if (existing) return existing;
  const created = uuid();
  setTrackingId(created);
  return created;
}

export function setTrackingId(id: string): void {
  setCookie("_tid", id, cookieOptions(YEAR));
}

/** At logout, so whoever uses the browser next is not taken for the user. */
export function clearTrackingId(): void {
  deleteCookie("_tid", cookieOptions());
}

/** The Google Analytics client id, from `_ga` (`GA1.1.<id>`). */
export function gaClientId(): string | undefined {
  return getCookie("_ga")?.match(/^GA\d\.\d\.(.+)$/)?.[1];
}

/** The Google Analytics session, from `_ga_<stream>`: what follows its version, up to the first dot. */
function gaSessionId(): string | undefined {
  return getCookie(`_ga_${GA4_STREAM}`)
    ?.match(/^GS\d\.\d\.(.+)$/)?.[1]
    .split(".")[0];
}

/** Copies the Google Analytics ids for the backend, once Analytics has set them. */
export function recordGaIds(): void {
  const sessionId = gaSessionId();
  if (sessionId) {
    setCookie("_sid", sessionId, cookieOptions(GA_SESSION));
  }
  const clientId = gaClientId();
  if (clientId) {
    setCookie("_did", clientId, cookieOptions(YEAR));
  }
}
